import React from "react";
import { View, Pressable, StyleSheet } from "react-native";
import { t } from "@constants/localization";
import {
  LOCK,
  OFD_STATE,
  STATUS,
  eddLabel,
  getBoxesWithName,
  getCardActions,
} from "../helpers";
import {
  C,
  RText,
  Tag,
  RouteIcon,
  ChevronRight,
  AddressLine,
  tpName,
} from "../theme";

const PATH = "DsRouting";

// Status pill colours from the mockup: PACKED green, PICKED BY DB blue,
// OFD ✓ green; NOT RACKED amber and NOT RECEIVED grey cover cases the
// mockup leaves out.
const STATUS_TAG = {
  [STATUS.NOT_RECEIVED]: {
    key: "notReceived",
    bg: C.disabledBg,
    color: C.muted,
  },
  [STATUS.NOT_RACKED]: { key: "notRacked", bg: C.amberBg, color: C.amber },
  [STATUS.PACKED]: { key: "packed", bg: C.greenBg, color: C.green },
  [STATUS.ROUTED]: { key: "packed", bg: C.greenBg, color: C.green },
  [STATUS.PICKED]: { key: "pickedByDb", bg: C.blueBg, color: C.blue },
  [STATUS.OFD]: { key: "ofd", bg: C.greenBg, color: C.green },
};

// "Recon pending · Ramesh · 1 Oct": an earlier day's OFD isn't closed.
const reconText = (block, today) => {
  const label = eddLabel(block?.date, today);
  const date = label?.text || block?.date || "";
  return t(`${PATH}/actions/reconPending`, {
    detail: [block?.db_name, date].filter(Boolean).join(" · "),
  });
};

const eddText = (date, today) => {
  const label = eddLabel(date, today);
  if (!label) return null;
  const value = label.key
    ? t(`${PATH}/edd${label.key === "today" ? "Today" : "Tomorrow"}`)
    : label.text;
  return t(`${PATH}/edd`, { date: value });
};

/**
 * One TP card, pixel-matched to the mockup (§10 frame 08): customer name ·
 * orders · status pill; locality · rack · EDD; a divider; then "+ Add DB",
 * or "DB · name" with an outlined Change, Recommended route and Mark OFD
 * (grey until the DB picks, green after), or "OFD ✓".
 */
const RoutingTpRow = ({
  item,
  today,
  onAddDb,
  onChange,
  onRecommended,
  onMarkOfd,
}) => {
  const tag = STATUS_TAG[item.status] || STATUS_TAG[STATUS.PACKED];
  const actions = getCardActions(item);
  const id = `${item.cx_id}-${item.date}`;
  const title = tpName(item);
  const place = [
    item.area,
    item.racks?.length
      ? t(`${PATH}/rack`, { racks: item.racks.join(", ") })
      : null,
    eddText(item.date, today),
  ]
    .filter(Boolean)
    .join(" · ");
  const onTrip = actions.lock === LOCK.LIVE_OFD;
  const deliveredToday = actions.lock === LOCK.DELIVERED_TODAY;
  const pickedByDb = actions.lock === LOCK.PICKED_BY_DB;
  const boxesWith = getBoxesWithName(item);
  const hasDbRow =
    item.db &&
    (actions.change || onTrip || pickedByDb || actions.ofd === OFD_STATE.DONE);
  const showDivider = actions.addDb || hasDbRow || deliveredToday;

  return (
    <View style={styles.card} testID={`ds-routing-tp-${id}`}>
      <View style={styles.head}>
        <RText size={17} weight={700} style={styles.tight}>
          {title}
        </RText>
        <RText size={12} weight={600} color={C.muted} style={styles.grow}>
          {t(`${PATH}/${item.order_count === 1 ? "order" : "orders"}`, {
            count: String(item.order_count ?? 0),
          })}
        </RText>
        <Tag
          label={t(`${PATH}/status/${tag.key}`)}
          bg={tag.bg}
          color={tag.color}
        />
      </View>
      <AddressLine address={item.address} style={styles.address} />
      {place ? (
        <RText size={12} weight={500} color={C.muted} style={styles.place}>
          {place}
        </RText>
      ) : null}

      {showDivider ? <View style={styles.divider} /> : null}

      {actions.addDb ? (
        <Pressable
          testID={`ds-routing-add-db-${id}`}
          onPress={() => onAddDb(item)}
          style={[styles.btn, styles.btnRed]}
        >
          <RText size={15} weight={600} color="#FFFFFF">
            {t(`${PATH}/actions/addDb`)}
          </RText>
        </Pressable>
      ) : null}

      {hasDbRow ? (
        <View style={styles.dbRow}>
          <RText size={14} weight={700} color={C.blue} style={styles.grow}>
            {pickedByDb
              ? t(`${PATH}/pickedByNote`, { name: item.db.name || "" })
              : t(`${PATH}/dbLine`, { name: item.db.name || "" })}
          </RText>
          {actions.change ? (
            <Pressable
              testID={`ds-routing-change-${id}`}
              onPress={() => onChange(item)}
              hitSlop={8}
              style={styles.change}
            >
              <RText size={12} weight={700} color={C.blueLine}>
                {t(`${PATH}/actions/change`)}
              </RText>
            </Pressable>
          ) : onTrip ? (
            <Tag
              label={t(`${PATH}/actions/onTrip`)}
              bg={C.blueBg}
              color={C.blue}
              size={10}
              style={styles.doneTag}
            />
          ) : (
            <Tag
              label={t(`${PATH}/actions/ofdDone`)}
              bg={C.greenBg}
              color={C.green}
              size={10}
              style={styles.doneTag}
            />
          )}
        </View>
      ) : null}

      {boxesWith ? (
        <RText size={12} weight={600} color={C.amber} style={styles.boxesWith}>
          {t(`${PATH}/boxesWith`, { name: boxesWith, db: item.db?.name || "" })}
        </RText>
      ) : null}

      {deliveredToday ? (
        <View style={[styles.btn, styles.btnDisabled, styles.noTop]}>
          <RText size={14} weight={600} color={C.disabledInk}>
            {t(`${PATH}/deliveredToday`)}
          </RText>
        </View>
      ) : null}

      {actions.recommended ? (
        <>
          <Pressable
            testID={`ds-routing-recommended-${id}`}
            onPress={() => onRecommended(item)}
            style={[styles.btn, styles.btnBlueOutline]}
          >
            <RouteIcon />
            <RText size={14} weight={600} color={C.blueLine}>
              {t(`${PATH}/actions/recommended`)}
            </RText>
            <ChevronRight color={C.blueLine} />
          </Pressable>
          {actions.ofd === OFD_STATE.READY ? (
            <Pressable
              testID={`ds-routing-ofd-${id}`}
              onPress={() => onMarkOfd(item)}
              style={[styles.btn, styles.btnGreen]}
            >
              <RText size={15} weight={600} color="#FFFFFF">
                {t(`${PATH}/actions/markOfd`)}
              </RText>
            </Pressable>
          ) : (
            <View style={[styles.btn, styles.btnDisabled]}>
              <RText size={14} weight={600} color={C.disabledInk}>
                {actions.ofd === OFD_STATE.BLOCKED
                  ? reconText(item.ofd_block, today)
                  : t(`${PATH}/actions/markOfdWaiting`)}
              </RText>
            </View>
          )}
        </>
      ) : null}
    </View>
  );
};

export { STATUS_TAG, eddText };
export default RoutingTpRow;

const styles = StyleSheet.create({
  card: {
    backgroundColor: C.surface,
    borderWidth: 1,
    borderColor: C.border,
    borderRadius: 12,
    padding: 15,
    marginHorizontal: 16,
    marginBottom: 12,
  },
  head: { flexDirection: "row", alignItems: "center", gap: 8 },
  tight: { letterSpacing: -0.2 },
  grow: { flex: 1 },
  address: { paddingTop: 4 },
  place: { paddingTop: 3 },
  divider: { height: 1, backgroundColor: C.border, marginVertical: 13 },
  dbRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 9,
  },
  change: {
    borderWidth: 1.5,
    borderColor: C.blueLine,
    borderRadius: 8,
    paddingVertical: 4,
    paddingHorizontal: 12,
  },
  doneTag: { paddingVertical: 5, paddingHorizontal: 12 },
  btn: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 7,
    borderRadius: 9,
    paddingVertical: 11,
  },
  btnRed: { backgroundColor: C.red, paddingVertical: 12 },
  btnGreen: { backgroundColor: C.green, paddingVertical: 12, marginTop: 8 },
  btnBlueOutline: {
    borderWidth: 1.5,
    borderColor: C.blueLine,
    paddingVertical: 10,
  },
  btnDisabled: { backgroundColor: C.disabledBg, marginTop: 8 },
  noTop: { marginTop: 0 },
  boxesWith: {
    backgroundColor: C.amberBg,
    borderRadius: 8,
    paddingVertical: 6,
    paddingHorizontal: 10,
    marginBottom: 9,
  },
});
